import React, { useEffect, useRef, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";
import { Pdf } from "../../assets/svg";
import { getSubjectServiceById } from "../../services/subject-service";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faBookOpen, faFilePdf, faAnglesRight, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { SnackbarContext } from "../../config/hooks/use-toast";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Slider,
  Menu,
  MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Replay5Icon from "@mui/icons-material/Replay5";
import Forward5Icon from "@mui/icons-material/Forward5";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SettingsIcon from "@mui/icons-material/Settings";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";
import SpeedIcon from "@mui/icons-material/Speed";

import { 
  deletePlaylistService, 
  createPlayListService,
  updatePlayListService,
  deleteItemPlaylistService,
  uploadItemPlayListService
} from "../../services/playList-service";
import AddIcon from "@mui/icons-material/Add";
import "./subject-detail.css";
import DashboardLayout from "../../shared/layout/DashboardLayout";
import { BASE_API_URL } from "../../config/api";

interface Video {
  id: number;
  title: string;
  url: string;
  isCompleted: boolean;
}

interface Resource {
  id: number;
  title: string;
  url: string;
  isCompleted: boolean;
}

interface Playlist {
  id: number;
  title: string;
  description: string;
  videos: Video[];
  qcms: Resource[];
  fiches: Resource[];
  exercices: Resource[];
  corrections: Resource[];
  [key: string]: any;
}

interface SubjectData {
  playLists: Playlist[];
}

interface SnackbarContextType {
  showToast?: (message: string, severity: "success" | "error") => void;
  showSnackbar?: (message: string, severity: "success" | "error") => void;
}

// YouTube Utility Functions
const getYouTubeVideoId = (url: string) => {
  const regex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const isYouTubeUrl = (url: string) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

const SubjectDetails = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  
  // Récupération du rôle utilisateur
  const role = useSelector((state: RootState) => state?.user?.userData?.role?.name);
  const canManageChapters = role === "ROLE_ADMIN" || role === "ROLE_SUPER_TEACHER" || role === "ROLE_TEACHER";

  const [subjectData, setSubjectData] = useState<SubjectData | null>(null);
  const [activeStatus, setActiveStatus] = useState<string>("videos");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activePlaylist, setActivePlaylist] = useState<Playlist | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteFileDialog, setOpenDeleteFileDialog] = useState(false);
  
  const [playlistToDelete, setPlaylistToDelete] = useState<Playlist | null>(null);
  const [playlistToEdit, setPlaylistToEdit] = useState<Playlist | null>(null);
  const [fileToDelete, setFileToDelete] = useState<{
    id: number;
    type: string;
    playlistId: number;
  } | null>(null);
  
  const [localSnackbar, setLocalSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error"
  });
  
  // YouTube API Integration
  const [ytPlayer, setYtPlayer] = useState<any>(null);
  const [ytIsPlaying, setYtIsPlaying] = useState(false);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytApiReady, setYtApiReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [availableQualities, setAvailableQualities] = useState<string[]>(['auto', 'hd1080', 'hd720', 'large', 'medium', 'small', 'tiny']);
  const [currentQuality, setCurrentQuality] = useState<string>('auto');
  const [availablePlaybackRates, setAvailablePlaybackRates] = useState<number[]>([1]);

  const [currentPlaybackRate, setCurrentPlaybackRate] = useState<number>(1);
  const [qualityAnchorEl, setQualityAnchorEl] = useState<null | HTMLElement>(null);
  const [playbackRateAnchorEl, setPlaybackRateAnchorEl] = useState<null | HTMLElement>(null);


  // Time Formatter
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Sync YouTube Time
  useEffect(() => {
    let interval: any;
    if (ytIsPlaying && ytPlayer) {
      interval = setInterval(() => {
        const current = ytPlayer.getCurrentTime();
        const total = ytPlayer.getDuration();
        setCurrentTime(current);
        setDuration(total);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [ytIsPlaying, ytPlayer]);

  const handleSeek = (event: Event, newValue: number | number[]) => {
    if (ytPlayer && typeof newValue === "number") {
      ytPlayer.seekTo(newValue, true);
      setCurrentTime(newValue);
    }
  };

  useEffect(() => {
    // Inject API script if not present
    const id = "youtube-iframe-api";
    if (!document.getElementById(id)) {
      const tag = document.createElement("script");
      tag.id = id;
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }

    const checkAPI = setInterval(() => {
      if ((window as any).YT && (window as any).YT.Player) {
        setYtApiReady(true);
        clearInterval(checkAPI);
      }
    }, 100);

    return () => clearInterval(checkAPI);
  }, []);

  useEffect(() => {
    if (!ytApiReady || !isYouTubeUrl(videoUrl)) {
      setYtPlayer(null);
      return;
    }

    const videoId = getYouTubeVideoId(videoUrl);
    if (!videoId) return;

    let player: any = null;
    const container = document.getElementById("yt-player");
    if (!container) return;

    setYtLoading(true);
    try {
      // @ts-ignore
      player = new (window as any).YT.Player("yt-player", {
        videoId: videoId,
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
        },
        events: {
          onReady: (e: any) => {
            setYtPlayer(e.target);
            setYtLoading(false);
            
            // Get initial qualities and rates
            try {
              if (e.target.getAvailableQualityLevels) {
                const levels = e.target.getAvailableQualityLevels();
                if (levels && levels.length > 0) {
                  setAvailableQualities(levels);
                } else {
                  // Some videos take time to report qualities, retry in 2s
                  setTimeout(() => {
                    const retryLevels = e.target.getAvailableQualityLevels?.();
                    if (retryLevels) setAvailableQualities(retryLevels);
                  }, 2000);
                }
                setCurrentQuality(e.target.getPlaybackQuality?.() || 'auto');
              }
              
              if (e.target.getAvailablePlaybackRates) {
                const rates = e.target.getAvailablePlaybackRates();
                setAvailablePlaybackRates(rates);
                setCurrentPlaybackRate(e.target.getPlaybackRate?.() || 1);
              }
            } catch (err) {
              console.warn("Quality/Rate detection error", err);
            }

          },
          onStateChange: (e: any) => {
            // @ts-ignore
            setYtIsPlaying(e.data === (window as any).YT.PlayerState.PLAYING);
            
            // Re-sync settings on play just in case they changed or were delayed
            // @ts-ignore
            if (e.data === (window as any).YT.PlayerState.PLAYING && ytPlayer) {
              try {
                const levels = ytPlayer.getAvailableQualityLevels?.();
                if (levels && levels.length > 0) setAvailableQualities(levels);
                
                const rates = ytPlayer.getAvailablePlaybackRates?.();
                if (rates) setAvailablePlaybackRates(rates);
              } catch (err) {}
            }

          },
          onPlaybackQualityChange: (e: any) => {
            if (e.data) setCurrentQuality(e.data);
          },
        },
      });

    } catch (err) {
      console.error("YT Player Init Error:", err);
      setYtLoading(false);
    }

    return () => {
      if (player && player.destroy) {
        try {
          player.destroy();
        } catch (e) {
          console.error("Destroy error", e);
        }
      }
      setYtPlayer(null);
      setYtIsPlaying(false);
    };
  }, [videoUrl, ytApiReady]);

  const handleYtSeek = (direction: 'back' | 'fwd') => {
    if (!ytPlayer) return;
    const currentTime = ytPlayer.getCurrentTime();
    const seekTo = direction === 'back' ? Math.max(0, currentTime - 5) : currentTime + 5;
    ytPlayer.seekTo(seekTo, true);
  };

  const handleYtPlayPause = () => {
    if (!ytPlayer) return;
    if (ytIsPlaying) ytPlayer.pauseVideo();
    else ytPlayer.playVideo();
  };

  // Quality Management
  const handleQualityClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setQualityAnchorEl(event.currentTarget);
    if (ytPlayer && ytPlayer.getAvailableQualityLevels) {
      try {
        const levels = ytPlayer.getAvailableQualityLevels();
        if (levels && levels.length > 0) {
          // Merge with auto if not present
          const newLevels = levels.includes('auto') ? levels : ['auto', ...levels];
          setAvailableQualities(newLevels);
        }
      } catch (e) {}
    }
  };


  const handleQualityClose = () => {
    setQualityAnchorEl(null);
  };

  const handleQualityChange = (quality: string) => {
    if (ytPlayer) {
      try {
        // Higher reliability: use range if available
        if (ytPlayer.setPlaybackQualityRange) {
          ytPlayer.setPlaybackQualityRange(quality, quality);
        } else {
          ytPlayer.setPlaybackQuality(quality);
        }
        
        // If playing, some players need a nudge (seek) to force buffer reload at new quality
        const isPlaying = ytPlayer.getPlayerState?.() === (window as any).YT.PlayerState.PLAYING;
        if (isPlaying) {
          const currentTime = ytPlayer.getCurrentTime();
          ytPlayer.seekTo(currentTime, true);
          // Also call play again just in case
          ytPlayer.playVideo();
        }
        
        setCurrentQuality(quality);
      } catch (err) {
        console.error("Quality switch failed", err);
      }

    }
    handleQualityClose();
  };


  const handlePlaybackRateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPlaybackRateAnchorEl(event.currentTarget);
    if (ytPlayer && ytPlayer.getAvailablePlaybackRates) {
      try {
        const rates = ytPlayer.getAvailablePlaybackRates();
        if (rates && rates.length > 0) setAvailablePlaybackRates(rates);
      } catch (e) {}
    }
  };


  const handlePlaybackRateClose = () => {
    setPlaybackRateAnchorEl(null);
  };

  const handlePlaybackRateChange = (rate: number) => {
    if (ytPlayer) {
      ytPlayer.setPlaybackRate(rate);
      setCurrentPlaybackRate(rate);
    }
    handlePlaybackRateClose();
  };


  const [newPlaylist, setNewPlaylist] = useState({
    title: "",
    description: "",
  });

  const [openAddFileDialog, setOpenAddFileDialog] = useState(false);
  const [newFileData, setNewFileData] = useState({
    title: "",
    segment: "",
    youtubeUrl: "",
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [editPlaylist, setEditPlaylist] = useState({
    title: "",
    description: "",
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const activePlaylistIdRef = useRef<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [fetchingDoc, setFetchingDoc] = useState(false);
  const controlsTimeoutRef = useRef<any>(null);

  const handleOpenDoc = async (url: string) => {
    if (!url) return;
    setFetchingDoc(true);
    setOpenDocDialog(true);
    
    try {
      let finalUrl = url;
      if (!url.startsWith('http')) {
        finalUrl = `${BASE_API_URL}${url.replace(/^\/+/, "")}`;
      }

      // Use Blob strategy to bypass Frame-Options for private files
      const response = await fetch(finalUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setDocUrl(blobUrl);
    } catch (err) {
      console.error("PDF Fetch Error:", err);
      // Fallback to direct URL if fetch fails
      setDocUrl(url.startsWith('http') ? url : `${BASE_API_URL}${url.replace(/^\/+/, "")}`);
    } finally {
      setFetchingDoc(false);
    }
  };

  const handleCloseDoc = () => {
    if (docUrl.startsWith('blob:')) {
      URL.revokeObjectURL(docUrl);
    }
    setOpenDocDialog(false);
    setDocUrl("");
  };

  const resetControlsTimer = React.useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (ytIsPlaying) setShowControls(false);
    }, 2500);
  }, [ytIsPlaying]);

  useEffect(() => {
    if (!ytIsPlaying) {
      setShowControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    } else {
      resetControlsTimer();
    }
  }, [ytIsPlaying, resetControlsTimer]);

  const handleToggleFullscreen = () => {
    if (!playerContainerRef.current) return;

    if (!document.fullscreenElement) {
      playerContainerRef.current.requestFullscreen().catch(err => {
        console.error("Fullscreen error:", err);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;

  const showToast = React.useCallback((message: string, severity: "success" | "error") => {
    if (snackbarContext?.showToast) {
      snackbarContext.showToast(message, severity);
    } else if (snackbarContext?.showSnackbar) {
      snackbarContext.showSnackbar(message, severity);
    } else {
      setLocalSnackbar({ open: true, message, severity });
    }
  }, [snackbarContext]);

  const fileTypeMapping: Record<string, string> = {
    Video: "videos",
    Fiche: "fiches",
    Exercice: "exercices",
    Correction: "corrections",
    QCM: "qcms"
  };

  const statusToTypeMap: Record<string, string> = {
    videos: 'Video',
    qcms: 'QCM',
    fiches: 'Fiche',
    exercices: 'Exercice',
    corrections: 'Correction'
  };
  const statusToSegmentMap: Record<string, string> = {
    videos: "video",
    qcms: "qcm",
    fiches: "fiche",
    exercices: "exercice",
    corrections: "correction",
  };

  const fetchSubjectData = React.useCallback(() => {
    getSubjectServiceById(Number(id))
      .then((res) => {
        const data = res.data;
        setSubjectData(data);
        if (data.playLists.length > 0) {
          const preferredId = activePlaylistIdRef.current;
          const selectedPlaylist = data.playLists.find(
            (pl: Playlist) => pl.id === preferredId
          ) || data.playLists[0];

          setActivePlaylist(selectedPlaylist);
          if (selectedPlaylist?.videos?.length > 0) {
            setVideoUrl(selectedPlaylist.videos[0].url);
          } else {
            setVideoUrl("");
          }
        } else {
          setActivePlaylist(null);
          setVideoUrl("");
        }
      })
      .catch((error) => {
        console.error("Failed to fetch subject:", error);
        showToast("Erreur lors du chargement du sujet", "error");
      });
  }, [id, showToast]);

  useEffect(() => {
    if (id) {
      fetchSubjectData();
    }
  }, [id, fetchSubjectData]);

  useEffect(() => {
    activePlaylistIdRef.current = activePlaylist?.id ?? null;
  }, [activePlaylist]);

  // Anti-Inspection and Content Protection
  useEffect(() => {
    const preventInspect = (e: KeyboardEvent) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) ||
        (e.ctrlKey && e.key === "U")
      ) {
        e.preventDefault();
        return false;
      }
    };

    const disableRightClick = (e: MouseEvent) => {
      e.preventDefault();
    };

    window.addEventListener("keydown", preventInspect);
    window.addEventListener("contextmenu", disableRightClick);

    // Console warning for thieves
    console.log(
      "%cATTENTION: Toute reproduction ou distribution non autorisée de ce contenu est strictement interdite.",
      "color: red; font-size: 20px; font-weight: bold;"
    );

    return () => {
      window.removeEventListener("keydown", preventInspect);
      window.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  const handleVideoClick = (url: string) => {
    setVideoUrl(url);
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  const buildStreamUrl = React.useCallback((url: string) => {
    if (!url || isYouTubeUrl(url)) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      const pathname = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
      const fileName = pathname || parsed.pathname;
      return `${BASE_API_URL}local-storage/stream-video?fileName=${encodeURIComponent(fileName)}`;
    } catch {
      const sanitized = url.replace(/^\/+/, "");
      return `${BASE_API_URL}local-storage/stream-video?fileName=${encodeURIComponent(sanitized)}`;
    }
  }, []);

  useEffect(() => {
    if (videoRef.current && !isYouTubeUrl(videoUrl)) {
      videoRef.current.src = buildStreamUrl(videoUrl);
      videoRef.current.setAttribute("controlslist", "nodownload");
    }
  }, [videoUrl, buildStreamUrl]);

  const handleStatusClick = (statusName: string) => {
    setActiveStatus(statusName);
  };

  const handlePlaylistClick = (playlist: Playlist) => {
    setActivePlaylist(playlist);
    setActiveStatus("videos");
    if (playlist.videos?.length > 0) {
      setVideoUrl(playlist.videos[0].url);
    }
  };

  const handleDeleteClick = (playlist: Playlist) => {
    setPlaylistToDelete(playlist);
    setOpenDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
    setPlaylistToDelete(null);
  };

  const handleCreateOpen = () => setOpenCreateDialog(true);
  const handleCreateClose = () => {
    setOpenCreateDialog(false);
    setNewPlaylist({ title: "", description: "" });
  };

  const handleEditClick = (playlist: Playlist) => {
    setPlaylistToEdit(playlist);
    setEditPlaylist({
      title: playlist.title,
      description: playlist.description || ""
    });
    setOpenEditDialog(true);
  };

  const handleEditClose = () => {
    setOpenEditDialog(false);
    setPlaylistToEdit(null);
    setEditPlaylist({ title: "", description: "" });
  };

  const handleDeleteFileClick = (fileId: number, fileType: string) => {
    if (!activePlaylist) return;
    
    setFileToDelete({
      id: fileId,
      type: fileType,
      playlistId: activePlaylist.id
    });
    setOpenDeleteFileDialog(true);
  };

  const handleDeleteFileClose = () => {
    setOpenDeleteFileDialog(false);
    setFileToDelete(null);
  };

  const handleDeletePlaylist = () => {
    if (!playlistToDelete || !subjectData) return;

    deletePlaylistService(playlistToDelete.id)
      .then(() => {
        const updatedPlaylists = subjectData.playLists.filter(
          pl => pl.id !== playlistToDelete.id
        );
        
        setSubjectData({
          ...subjectData,
          playLists: updatedPlaylists
        });
        
        if (activePlaylist?.id === playlistToDelete.id) {
          const newActivePlaylist = updatedPlaylists[0] || null;
          setActivePlaylist(newActivePlaylist);
          setVideoUrl(newActivePlaylist?.videos?.[0]?.url || "");
        }
        
        showToast("Chapitre supprimé avec succès", "success");
      })
      .catch((error) => {
        console.error("Failed to delete playlist:", error);
        showToast("Échec de la suppression du chapitre", "error");
      })
      .finally(() => {
        handleDeleteClose();
      });
  };

  const handleCreatePlaylist = () => {
    if (!id) {
      showToast("ID du sujet non disponible", "error");
      return;
    }

    if (!newPlaylist.title.trim()) {
      showToast("Veuillez saisir un titre pour le chapitre", "error");
      return;
    }

    createPlayListService(Number(id), newPlaylist)
      .then(() => {
        fetchSubjectData();
        showToast("Chapitre créé avec succès", "success");
        handleCreateClose();
      })
      .catch((error) => {
        console.error("Failed to create playlist:", error);
        showToast("Échec de la création du chapitre", "error");
      });
  };

  const handleUpdatePlaylist = () => {
    if (!playlistToEdit || !id) {
      showToast("Données de modification invalides", "error");
      return;
    }

    if (!editPlaylist.title.trim()) {
      showToast("Veuillez saisir un titre pour le chapitre", "error");
      return;
    }

    updatePlayListService(playlistToEdit.id, Number(id), {
      title: editPlaylist.title,
      description: editPlaylist.description
    })
      .then(() => {
        fetchSubjectData();
        showToast("Chapitre modifié avec succès", "success");
        
        if (activePlaylist?.id === playlistToEdit.id) {
          setActivePlaylist({
            ...activePlaylist,
            title: editPlaylist.title,
            description: editPlaylist.description
          });
        }
        
        handleEditClose();
      })
      .catch((error) => {
        console.error("Failed to update playlist:", error);
        showToast("Échec de la modification du chapitre", "error");
      });
  };

  const handleDeleteFile = () => {
    if (!fileToDelete || !subjectData) return;

    deleteItemPlaylistService(fileToDelete.playlistId, fileToDelete.id)
      .then(() => {
        const updatedPlaylists = subjectData.playLists.map(playlist => {
          if (playlist.id === fileToDelete.playlistId) {
            const resourceType = fileTypeMapping[fileToDelete.type];
            return {
              ...playlist,
              [resourceType]: playlist[resourceType].filter(
                (item: any) => item.id !== fileToDelete.id
              )
            };
          }
          return playlist;
        });

        setSubjectData({
          ...subjectData,
          playLists: updatedPlaylists
        });

        if (activePlaylist && activePlaylist.id === fileToDelete.playlistId) {
          const updatedActivePlaylist = updatedPlaylists.find(
            pl => pl.id === activePlaylist.id
          );
          if (updatedActivePlaylist) {
            setActivePlaylist(updatedActivePlaylist);
          }
        }

        showToast("Fichier supprimé avec succès", "success");
      })
      .catch(error => {
        console.error("Failed to delete file:", error);
        showToast("Échec de la suppression du fichier", "error");
      })
      .finally(() => {
        handleDeleteFileClose();
      });
  };

  const getFilteredResources = () => {
    if (!activePlaylist) return [];

    switch (activeStatus) {
      case "videos": return activePlaylist.videos || [];
      case "qcms": return activePlaylist.qcms || [];
      case "fiches": return activePlaylist.fiches || [];
      case "exercices": return activePlaylist.exercices || [];
      case "corrections": return activePlaylist.corrections || [];
      default: return [];
    }
  };

  const statuses = ["videos", "qcms", "fiches", "exercices", "corrections"];
  const filteredResources = getFilteredResources();

  const preventContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  const handleAddFileOpen = () => {
    const defaultSegment = statusToSegmentMap[activeStatus] || "video";
    setNewFileData({
      title: "",
      segment: defaultSegment,
      youtubeUrl: "",
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setOpenAddFileDialog(true);
  };

  const handleAddFileClose = () => {
    setOpenAddFileDialog(false);
    setNewFileData({
      title: "",
      segment: "",
      youtubeUrl: "",
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadingFile(false);
  };

  const handleAddFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    if (name === "segment" && value === "youtube") {
      setSelectedFile(null);
    }

    if (name === "segment" && value !== "youtube") {
      setNewFileData((prev) => ({ ...prev, youtubeUrl: "" }));
    }

    setNewFileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileToUpload = event.target.files?.[0];
    if (fileToUpload) {
      setSelectedFile(fileToUpload);
    }
  };

  const handleUploadFile = async () => {
    if (!id || !activePlaylist) {
      showToast("Sujet ou chapitre introuvable", "error");
      return;
    }

    if (!newFileData.title.trim()) {
      showToast("Veuillez saisir un titre pour le fichier", "error");
      return;
    }

    const selectedCategory = newFileData.segment || statusToSegmentMap[activeStatus] || "video";
    const allowedVideoExtensions = ["mp4", "avi", "mov", "mkv"];

    if (selectedCategory === "youtube") {
      if (!newFileData.youtubeUrl.trim()) {
        showToast("Veuillez saisir l'URL YouTube", "error");
        return;
      }

      const youtubeUrlPattern = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/;
      if (!youtubeUrlPattern.test(newFileData.youtubeUrl)) {
        showToast("Veuillez saisir une URL YouTube valide", "error");
        return;
      }

      const formSent = new FormData();
      const formSentJson = {
        category: "youtube",
        title: newFileData.title,
        youtubeUrl: newFileData.youtubeUrl,
      };

      formSent.append("subjectId", id);
      formSent.append("uploadItemRequestJson", JSON.stringify(formSentJson));

      setUploadingFile(true);
      setUploadProgress(0);

      try {
        await uploadItemPlayListService(
          activePlaylist.id,
          formSent,
          () => {
            setUploadProgress(100);
          }
        );
        showToast("URL YouTube ajoutée avec succès", "success");
        handleAddFileClose();
        fetchSubjectData();
      } catch (error) {
        console.error("Error uploading YouTube URL:", error);
        showToast("Erreur lors de l'ajout de l'URL YouTube", "error");
      } finally {
        setUploadingFile(false);
      }

      return;
    }

    if (!selectedFile) {
      showToast("Veuillez sélectionner un fichier", "error");
      return;
    }

    const fileExtension = selectedFile.name?.split(".").pop()?.toLowerCase();

    if (!fileExtension) {
      showToast(
        "Impossible de déterminer le type de fichier. Veuillez vérifier le fichier et réessayer.",
        "error"
      );
      return;
    }

    const isVideo = allowedVideoExtensions.includes(fileExtension);
    const isPdf = fileExtension === "pdf";

    if (!isVideo && !isPdf) {
      showToast(
        "Le fichier n'est ni une vidéo ni un PDF. Veuillez vérifier le fichier et réessayer.",
        "error"
      );
      return;
    }

    if (isVideo && selectedCategory !== "video") {
      showToast(
        "Le fichier est une vidéo, mais la catégorie n'est pas définie sur 'vidéo'. Veuillez corriger votre sélection.",
        "error"
      );
      return;
    }

    if (isPdf && selectedCategory === "video") {
      showToast(
        "La catégorie sélectionnée n'est pas 'vidéo' et le fichier n'est pas un PDF. Veuillez vérifier vos sélections.",
        "error"
      );
      return;
    }

    const formSent = new FormData();
    const formSentJson = {
      category: selectedCategory,
      title: newFileData.title,
    };
    formSent.append("subjectId", id);
    formSent.append("uploadItemRequestJson", JSON.stringify(formSentJson));
    formSent.append("file", selectedFile as Blob);

    setUploadingFile(true);
    setUploadProgress(0);

    try {
      await uploadItemPlayListService(
        activePlaylist.id,
        formSent,
        (progressEvent: any) => {
          if (progressEvent?.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            setUploadProgress(progress);
          }
        }
      );

      showToast("Fichier téléchargé avec succès", "success");
      handleAddFileClose();
      fetchSubjectData();
    } catch (error) {
      console.error("Error uploading file:", error);
      showToast("Erreur lors du téléchargement du fichier", "error");
    } finally {
      setUploadingFile(false);
    }
  };

  // Apply custom styles
  useEffect(() => {
    const customScrollbarStyle = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: linear-gradient(to bottom, #09745f, #07b98e);
        border-radius: 3px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(to bottom, #07b98e, #09745f);
      }
      .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = customScrollbarStyle;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <DashboardLayout>
      <div className="flex flex-col items-center px-4 pb-20 space-y-6 md:px-12">
        <div className="flex flex-col w-full gap-6 max-w-7xl lg:flex-row">
          {/* Video Player Section */}
          <div className="flex-1 overflow-hidden bg-white shadow-[0_30px_60px_rgba(0,0,0,0.05)] rounded-[2.5rem] border border-gray-100">
            <div className="relative overflow-hidden aspect-video bg-title rounded-t-[2.5rem]">
              {videoUrl && isYouTubeUrl(videoUrl) ? (
                <div 
                  ref={playerContainerRef} 
                  className={`relative w-full h-full bg-black overflow-hidden group/yt transition-all duration-300 ${!showControls && ytIsPlaying ? 'cursor-none' : 'cursor-default'}`}
                  onContextMenu={preventContextMenu}
                  onMouseMove={resetControlsTimer}
                >
                  {/* The actual YouTube iframe (Controlled by API) */}
                  <div id="yt-player" className="w-full h-full pointer-events-none" />
                  
                  {/* Security Shield (Intercepts all clicks at z-10) */}
                  <div className="absolute inset-0 z-10 cursor-default bg-transparent pointer-events-auto" />
                  
                  {/* PINPOINT SECURITY PATCHES (Shadow Masks over leak points) */}
                  {/* Top Mask (Blocks Title & Share button) */}
                  <div className="absolute top-0 left-0 w-full h-16 z-20 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
                  
                  {/* Bottom-Right Edge Mask (Blocks 'Watch on YouTube' logo) */}
                  <div className="absolute bottom-0 right-0 w-48 h-16 z-20 bg-gradient-to-tl from-black/80 to-transparent pointer-events-none" />

                  {/* Loading State Overlay */}
                  {ytLoading && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-zinc-950">
                       <div className="flex flex-col items-center gap-4">
                          <CircularProgress sx={{ color: '#09745f' }} />
                          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">Chargement sécurisé...</p>
                       </div>
                    </div>
                  )}

                  {/* Anti-Leak Custom Controls Layer */}
                  <div className={`absolute inset-x-0 bottom-0 z-30 transition-all duration-500 pb-8 px-8 ${showControls || !ytIsPlaying ? 'opacity-100' : 'opacity-0'}`}>
                    <div className={`w-full max-w-4xl mx-auto bg-black/60 backdrop-blur-2xl px-8 py-5 rounded-[2.5rem] border border-white/10 shadow-2xl transition-all duration-500
                      ${(showControls || !ytIsPlaying || ytLoading) ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
                    `}>
                      {/* Unified Progress Bar */}
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-[10px] font-black text-white/50 w-10 text-right">{formatTime(currentTime)}</span>
                        <Slider
                          size="small"
                          value={currentTime}
                          min={0}
                          max={duration || 100}
                          onChange={handleSeek}
                          sx={{
                            color: '#09745f',
                            height: 4,
                            '& .MuiSlider-thumb': {
                              width: 14,
                              height: 14,
                              transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                              '&:before': {
                                boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
                              },
                              '&:hover, &.Mui-focusVisible': {
                                boxShadow: '0px 0px 0px 8px rgba(9, 116, 95, 0.16)',
                              },
                              '&.Mui-active': {
                                width: 22,
                                height: 22,
                              },
                            },
                            '& .MuiSlider-track': {
                              border: 'none',
                              background: 'linear-gradient(90deg, #09745f, #07b98e)',
                            },
                            '& .MuiSlider-rail': {
                              opacity: 0.15,
                              backgroundColor: '#fff',
                            },
                          }}
                        />
                        <span className="text-[10px] font-black text-white/50 w-10 text-left">{formatTime(duration)}</span>
                      </div>

                      {/* Unified Action Buttons */}
                      <div className="flex items-center justify-between gap-4">
                         <div className="flex-1 flex justify-center items-center gap-6 pl-14">
                            <IconButton 
                              className="!text-white/60 hover:!text-white hover:!bg-white/10 !transition-all !w-11 !h-11" 
                              onClick={() => handleYtSeek('back')}
                              disabled={ytLoading}
                            >
                              <Replay5Icon sx={{ fontSize: 22 }} />
                            </IconButton>
                            
                            <IconButton 
                              className="!text-white !w-14 !h-14 bg-primary/80 hover:bg-primary shadow-lg shadow-primary/20 hover:scale-105 !transition-all border-none"
                              onClick={handleYtPlayPause}
                              disabled={ytLoading}
                            >
                              {ytIsPlaying ? <PauseIcon sx={{ fontSize: 28 }} /> : <PlayArrowIcon sx={{ fontSize: 28 }} />}
                            </IconButton>
                            
                            <IconButton 
                              className="!text-white/60 hover:!text-white hover:!bg-white/10 !transition-all !w-11 !h-11" 
                              onClick={() => handleYtSeek('fwd')}
                              disabled={ytLoading}
                            >
                              <Forward5Icon sx={{ fontSize: 22 }} />
                            </IconButton>
                         </div>

                          {/* Side Controls */}
                          <div className="flex items-center gap-2">
                            <IconButton 
                              className="!text-white/60 hover:!text-white hover:!bg-white/10 !transition-all !w-11 !h-11" 
                              onClick={handlePlaybackRateClick}
                            >
                              <SlowMotionVideoIcon sx={{ fontSize: 22 }} />
                            </IconButton>

                            <IconButton 
                              className="!text-white/60 hover:!text-white hover:!bg-white/10 !transition-all !w-11 !h-11" 
                              onClick={handleQualityClick}
                            >
                              <SettingsIcon sx={{ fontSize: 22 }} />
                            </IconButton>

                            <IconButton 
                              className="!text-white/60 hover:!text-white hover:!bg-white/10 !transition-all !w-11 !h-11" 
                              onClick={handleToggleFullscreen}
                            >
                              {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 24 }} /> : <FullscreenIcon sx={{ fontSize: 24 }} />}
                            </IconButton>
                          </div>

                          {/* Playback Rate Menu */}
                          <Menu
                            anchorEl={playbackRateAnchorEl}
                            open={Boolean(playbackRateAnchorEl)}
                            onClose={handlePlaybackRateClose}
                            container={playerContainerRef.current}
                            slotProps={{
                               paper: {
                                 sx: {
                                   bgcolor: 'rgba(20, 20, 20, 0.9)',
                                   backdropFilter: 'blur(20px)',
                                   border: '1px solid rgba(255, 255, 255, 0.05)',
                                   borderRadius: '1.25rem',
                                   mt: -1,
                                   '& .MuiMenuItem-root': {
                                     fontSize: '10px',
                                     fontWeight: '900',
                                     textTransform: 'uppercase',
                                     letterSpacing: '0.1em',
                                     color: 'rgba(255, 255, 255, 0.5)',
                                     py: 1.5,
                                     px: 3,
                                     '&:hover': {
                                       bgcolor: 'rgba(255, 255, 255, 0.05)',
                                       color: '#fff',
                                     },
                                     '&.Mui-selected': {
                                       bgcolor: 'rgba(9, 116, 95, 0.2)',
                                       color: '#09745f',
                                       '&:hover': {
                                         bgcolor: 'rgba(9, 116, 95, 0.3)',
                                         color: '#09745f',
                                       },
                                     },
                                   },
                                 },
                               },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                          >
                            <div className="px-4 py-2 border-b border-white/5 mb-1">
                              <p className="text-[9px] font-black text-white/30 uppercase tracking-widest">Vitesse de Lecture</p>
                            </div>
                            {availablePlaybackRates.map((rate) => (
                              <MenuItem 
                                key={rate} 
                                selected={currentPlaybackRate === rate}
                                onClick={() => handlePlaybackRateChange(rate)}
                              >
                                {rate === 1 ? 'Normale' : `${rate}x`}
                              </MenuItem>
                            ))}
                          </Menu>

                         {/* Quality Menu */}

                         <Menu
                           anchorEl={qualityAnchorEl}
                           open={Boolean(qualityAnchorEl)}
                           onClose={handleQualityClose}
                           container={playerContainerRef.current}
                           slotProps={{
                              paper: {
                                sx: {
                                  bgcolor: 'rgba(20, 20, 20, 0.9)',
                                  backdropFilter: 'blur(20px)',
                                  border: '1px solid rgba(255, 255, 255, 0.05)',
                                  borderRadius: '1.25rem',
                                  mt: -1,
                                  '& .MuiMenuItem-root': {
                                    fontSize: '10px',
                                    fontWeight: '900',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    py: 1.5,
                                    px: 3,
                                    '&:hover': {
                                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                                      color: '#fff',
                                    },
                                    '&.Mui-selected': {
                                      bgcolor: 'rgba(9, 116, 95, 0.2)',
                                      color: '#09745f',
                                      '&:hover': {
                                        bgcolor: 'rgba(9, 116, 95, 0.3)',
                                      },
                                    },
                                  },
                                },
                              },
                           }}
                           transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                           anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                         >
                           <div className="px-4 py-2 border-b border-white/5 mb-1">
                             <p className="text-[9px] font-black text-white/30 uppercase tracking-widest">Qualité Vidéo</p>
                           </div>
                           {availableQualities.map((q) => (
                             <MenuItem 
                               key={q} 
                               selected={currentQuality === q}
                               onClick={() => handleQualityChange(q)}
                             >
                               {q === 'auto' ? 'Automatique' : 
                                 q === 'hd1080' ? '1080p (HD)' :
                                 q === 'hd720' ? '720p (HD)' :
                                 q === 'large' ? '480p' :
                                 q === 'medium' ? '360p' :
                                 q === 'small' ? '240p' :
                                 q === 'tiny' ? '144p' : q}
                             </MenuItem>
                           ))}
                         </Menu>
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <video
                  key={videoUrl}
                  ref={videoRef}
                  className="object-cover w-full h-full"
                  controls
                  controlsList="nodownload"
                  onContextMenu={preventContextMenu}
                  playsInline
                  preload="auto"
                >
                  <source src={videoUrl} />
                  Your browser does not support the video tag.
                </video>
              )}
              {!videoUrl && (
                <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-900 to-gray-800">
                  <div className="text-center text-white/50">
                    <OndemandVideoIcon sx={{ fontSize: 80 }} className="mb-4 animate-pulse" />
                    <p className="text-lg font-black uppercase tracking-widest ">Sélectionnez une leçon</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Video Title */}
            <div className="p-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-1.5 bg-primary/10 text-primary rounded-full text-[10px] font-black uppercase tracking-widest border border-primary/20">Leçon Actuelle</span>
              </div>
              <h1 className="mb-4 text-3xl font-black text-title leading-tight">
                {activePlaylist?.title || "Prêt à apprendre ?"}
              </h1>
              {activePlaylist?.description && (
                <p className="leading-relaxed text-text/70 text-lg">
                  {activePlaylist.description}
                </p>
              )}
            </div>
          </div>

          {/* Chapters Sidebar */}
          <div className={`transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] flex flex-col gap-6 ${isSidebarCollapsed ? "lg:w-24" : "lg:w-96"}`}>
            <div className="overflow-hidden bg-white shadow-[0_30px_60px_rgba(0,0,0,0.05)] rounded-[2.5rem] border border-gray-100 flex flex-col h-full">
              <div className={`p-6 border-b border-gray-100 flex items-center bg-gray-50/50 transition-all ${isSidebarCollapsed ? "justify-center" : "justify-between"}`}>
                  <h2 className={`flex items-center text-lg font-black text-title uppercase tracking-widest transition-all duration-500 ${isSidebarCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"}`}>
                    <FontAwesomeIcon icon={faBookOpen} className="mr-3 text-primary" />
                    Sommaire
                  </h2>
                  
                  <div className="flex items-center gap-3">
                    {canManageChapters && !isSidebarCollapsed && (
                      <button
                        onClick={handleCreateOpen}
                        className="w-10 h-10 flex items-center justify-center bg-primary text-white rounded-xl hover:shadow-2xl hover:scale-110 transition-all duration-300"
                      >
                        <AddIcon sx={{ fontSize: 20 }} />
                      </button>
                    )}
                    <button 
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                      className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-100 text-text/30 hover:text-primary hover:border-primary/30 transition-all duration-500 ${isSidebarCollapsed ? "rotate-180" : ""}`}
                    >
                      <FontAwesomeIcon icon={faAnglesRight} className="text-xs" />
                    </button>
                  </div>
              </div>
              
              <div className={`flex-1 max-h-[600px] overflow-y-auto p-4 space-y-2 custom-scrollbar transition-all ${isSidebarCollapsed ? "px-2" : "px-4"}`}>
                {subjectData?.playLists.length === 0 ? (
                  <div className="p-10 text-center text-gray-400">
                    {!isSidebarCollapsed && (
                      <>
                        <p className="font-bold uppercase text-[10px] tracking-widest mb-2">Aucun Chapitre</p>
                        <p className="text-xs">Le contenu sera bientôt disponible.</p>
                      </>
                    )}
                  </div>
                ) : (
                  subjectData?.playLists.map((playlist, index) => (
                    <div
                      key={playlist.id}
                      onClick={() => handlePlaylistClick(playlist)}
                      className={`group relative p-3.5 rounded-2xl transition-all duration-500 cursor-pointer border ${
                        playlist.id === activePlaylist?.id 
                          ? "bg-primary shadow-2xl shadow-primary/20 border-primary translate-x-1" 
                          : "bg-white border-gray-100 hover:bg-gray-50/80 hover:border-primary/20"
                      } ${isSidebarCollapsed ? "flex justify-center" : ""}`}
                    >
                      <div className={`flex items-start ${isSidebarCollapsed ? "justify-center" : "gap-4"}`}>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-xs shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                          playlist.id === activePlaylist?.id 
                            ? "bg-white/20 text-white" 
                            : "bg-primary/10 text-primary"
                        }`}>
                          {(index + 1).toString().padStart(2, '0')}
                        </div>
                        
                        {!isSidebarCollapsed && (
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-black text-[13px] uppercase tracking-wider mb-1 truncate ${
                              playlist.id === activePlaylist?.id ? "text-white" : "text-title"
                            }`}>
                              {playlist.title}
                            </h3>
                            <div className={`flex items-center gap-3 text-[9px] font-black uppercase tracking-widest ${
                              playlist.id === activePlaylist?.id ? "text-white/70" : "text-text/40"
                            }`}>
                              <span className="flex items-center gap-1">
                                <OndemandVideoIcon className="text-[12px]" />
                                {playlist.videos?.length || 0}
                              </span>
                              <span className="w-1 h-1 bg-current opacity-30 rounded-full"></span>
                              <span>{playlist.fiches?.length || 0} PDFs</span>
                            </div>
                          </div>
                        )}

                        {canManageChapters && !isSidebarCollapsed && (
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30"
                              onClick={(e) => { e.stopPropagation(); handleEditClick(playlist); }}
                            >
                              <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
                            </button>
                            <button
                              className="p-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600"
                              onClick={(e) => { e.stopPropagation(); handleDeleteClick(playlist); }}
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Resources Section */}
        <div className="w-full max-w-7xl mt-12">
          <div className="bg-white shadow-[0_40px_100px_rgba(0,0,0,0.08)] rounded-[3.5rem] border border-gray-100 p-10 md:p-14 overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-[#02c39a] to-blue-500"></div>
            
            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-16 gap-8">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-1 bg-primary rounded-full"></div>
                  <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em]">Bibliothèque de cours</span>
                </div>
                <h2 className="text-4xl font-black text-title uppercase tracking-tight">
                  Ressources pédagogiques
                </h2>
              </div>

              {/* High-End Tabs Navigation */}
              <div className="flex flex-wrap bg-gray-50/50 backdrop-blur-sm p-2 rounded-[2rem] border border-gray-100/50 shadow-inner">
                {statuses.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusClick(status)}
                    className={`px-8 py-4 rounded-[1.5rem] text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-500 flex items-center gap-3 ${
                      status === activeStatus
                        ? "bg-white text-primary shadow-[0_15px_30px_rgba(0,0,0,0.08)] scale-105"
                        : "text-text/30 hover:text-text/60 hover:bg-white/40"
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full transition-all duration-500 ${status === activeStatus ? "bg-primary scale-110 shadow-[0_0_10px_rgba(9,116,95,0.5)]" : "bg-gray-300"}`}></div>
                    {status === "videos" ? "Vidéos" : status}
                  </button>
                ))}
              </div>
            </div>

            {/* Premium Resources Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {!activePlaylist ? (
                <div className="col-span-full py-32 flex flex-col items-center justify-center text-center">
                  <div className="w-24 h-24 bg-gray-50 rounded-3xl flex items-center justify-center mb-8 border border-gray-100 animate-bounce">
                    <FontAwesomeIcon icon={faBookOpen} className="text-3xl text-primary/30" />
                  </div>
                  <h3 className="text-2xl font-black text-title uppercase tracking-widest mb-4">Sélectionnez un chapitre</h3>
                  <p className="text-sm font-bold text-text/40 max-w-sm leading-relaxed">Le savoir vous attend. Choisissez un module dans la liste latérale pour explorer ses ressources.</p>
                </div>
              ) : filteredResources.length === 0 ? (
                <div className="col-span-full py-32 flex flex-col items-center justify-center text-center">
                  <div className="w-24 h-24 bg-gray-50 rounded-3xl flex items-center justify-center mb-8 border border-gray-100">
                    <FontAwesomeIcon icon={faBookOpen} className="text-3xl text-primary/20" />
                  </div>
                  <h3 className="text-2xl font-black text-title uppercase tracking-widest mb-4">Section vide</h3>
                  <p className="text-sm font-bold text-text/40">Aucune ressource disponible pour la section <span className="text-primary italic">"{activeStatus}"</span>.</p>
                </div>
              ) : (
                filteredResources
                  .sort((a, b) => a.id - b.id)
                  .map((item, i) => (
                    <div
                      key={item.id}
                      className="group relative bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-[0_20px_60px_rgba(0,0,0,0.03)] transition-all duration-700 hover:shadow-[0_40px_100px_rgba(0,0,0,0.12)] hover:-translate-y-4 flex flex-col h-full animate-in fade-in slide-in-from-bottom duration-700"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <div className="flex items-start justify-between mb-8">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-700 ${
                           activeStatus === "videos" 
                             ? "bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white" 
                             : "bg-red-50 text-red-600 group-hover:bg-red-600 group-hover:text-white"
                        }`}>
                          {activeStatus === "videos" ? (
                            <OndemandVideoIcon sx={{ fontSize: 32 }} />
                          ) : (
                            <FontAwesomeIcon icon={faFilePdf} className="text-2xl" />
                          )}
                        </div>
                        
                        {canManageChapters && (
                          <div className="flex gap-2">
                             <button
                              onClick={() => handleDeleteFileClick(
                                item.id, 
                                statusToTypeMap[activeStatus]
                              )}
                              className="w-8 h-8 rounded-lg bg-red-50 text-red-500/40 hover:bg-red-500 hover:text-white transition-all duration-300"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 mb-10">
                         <div className="flex items-center gap-2 mb-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/40 group-hover:bg-primary group-hover:scale-150 transition-all duration-500"></span>
                            <span className="text-[9px] font-black text-text/30 uppercase tracking-[0.2em]">{activeStatus === "videos" ? "Leçon Vidéo" : "Support PDF"}</span>
                         </div>
                         <h4 className="font-black text-title text-lg uppercase leading-[1.4] line-clamp-2 tracking-tight group-hover:text-primary transition-colors duration-500">
                           {item.title}
                         </h4>
                      </div>
                      
                       <button
                          onClick={() => 
                           activeStatus === "videos" 
                             ? handleVideoClick(item.url) 
                             : handleOpenDoc(item.url)
                         }
                        className="w-full py-5 rounded-2xl bg-[#05322B] text-white font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-black/20 hover:bg-primary hover:-translate-y-1 transition-all duration-500 group-hover:shadow-primary/30"
                      >
                         {activeStatus === "videos" ? "Démarrer" : "Consulter"}
                      </button>
                    </div>
                  ))
              )}
              
              {canManageChapters && activePlaylist && (
                <div
                  onClick={handleAddFileOpen}
                  className="flex flex-col items-center justify-center p-10 border-2 border-dashed border-gray-100 rounded-[2.5rem] cursor-pointer bg-gray-50/20 hover:bg-white hover:border-primary/50 hover:shadow-2xl transition-all duration-700 group min-h-[340px]"
                >
                  <div className="w-16 h-16 bg-white shadow-[0_15px_30px_rgba(0,0,0,0.05)] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-500 border border-gray-50">
                    <AddIcon sx={{ fontSize: 32 }} />
                  </div>
                  <p className="text-title/50 font-black uppercase text-[11px] tracking-[0.25em] text-center">Ajouter {statusToTypeMap[activeStatus] || activeStatus}</p>
                </div>
              )}
          </div>
        </div>
      </div>

        {/* Dialog pour supprimer un chapitre */}
        <Dialog 
          open={openDeleteDialog} 
          onClose={handleDeleteClose} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{ 
            sx: { 
              borderRadius: '2.5rem', 
              p: 0, 
              border: '1px solid rgba(0,0,0,0.05)', 
              boxShadow: '0 40px 100px rgba(0,0,0,0.15)',
              overflow: 'hidden'
            } 
          }}
        >
          <div className="relative overflow-hidden bg-white">
            {/* Top accent bar */}
            <div className="h-2 w-full bg-gradient-to-r from-red-600 via-rose-500 to-orange-500"></div>
            
            <div className="p-8 md:p-12">
              <div className="flex flex-col items-center text-center">
                 {/* Glowing Icon Container */}
                 <div className="w-24 h-24 bg-red-50 rounded-[2rem] flex items-center justify-center text-red-500 mb-8 relative group">
                    <div className="absolute inset-0 bg-red-500/20 rounded-[2rem] blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-60"></div>
                    <div className="relative w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl shadow-red-500/10 border border-red-100">
                       <FontAwesomeIcon icon={faTrash} className="text-2xl" />
                    </div>
                 </div>

                 <div className="space-y-3">
                   <h5 className="text-[10px] font-black text-rose-500 uppercase tracking-[0.4em]">Action Irréversible</h5>
                   <h3 className="text-2xl md:text-3xl font-black text-title uppercase tracking-tight">Supprimer le Chapitre</h3>
                 </div>

                 <div className="mt-8 p-6 bg-red-50/50 rounded-3xl border border-red-100/50 max-w-md">
                    <p className="text-sm font-bold text-text/60 leading-relaxed">
                       Attention : cette opération supprimera <span className="text-title font-black underline decoration-red-500/30 decoration-2">définitivement</span> le chapitre <span className="text-red-500 italic">"{playlistToDelete?.title}"</span> ainsi que toutes ses leçons et ressources.
                    </p>
                 </div>

                 <div className="flex flex-col sm:flex-row gap-4 w-full mt-10">
                   <button 
                     onClick={handleDeleteClose}
                     className="flex-1 py-5 bg-white border-2 border-gray-100 text-title font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-95"
                   >
                     Annuler
                   </button>
                   <button 
                     onClick={handleDeletePlaylist}
                     className="flex-1 py-5 bg-[#E11D48] text-white font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-red-500/40 hover:bg-red-700 hover:-translate-y-1 transition-all active:scale-95 shadow-[0_20px_40px_-10px_rgba(225,29,72,0.4)]"
                   >
                     Supprimer
                   </button>
                 </div>
              </div>
            </div>
            
            <IconButton 
              onClick={handleDeleteClose} 
              className="!absolute top-6 right-6 !bg-gray-50 hover:!bg-red-50 hover:!text-red-500 transition-all !w-10 !h-10 border border-gray-100"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* Background decorative blobs */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-500/5 blur-[100px] pointer-events-none rounded-full"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orange-500/5 blur-[100px] pointer-events-none rounded-full"></div>
          </div>
        </Dialog>

        {/* Dialog pour créer un nouveau chapitre */}
        <Dialog 
          open={openCreateDialog} 
          onClose={handleCreateClose} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: '2.5rem', p: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 40px 100px rgba(0,0,0,0.15)' } }}
        >
          <Box className="p-4">
            <DialogTitle className="flex items-center justify-between !p-0 mb-10">
              <div className="flex flex-col gap-1">
                 <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em]">Module d'Apprentissage</span>
                 <p className="text-3xl text-title font-black uppercase tracking-tight">Nouveau Chapitre</p>
              </div>
              <IconButton onClick={handleCreateClose} className="!bg-gray-50 hover:!bg-white hover:shadow-xl transition-all">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent className="!p-0 space-y-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Titre du chapitre *</label>
                  <input
                    type="text"
                    placeholder="Ex: Introduction à l'Analyse Mathématique"
                    value={newPlaylist.title}
                    onChange={(e) => setNewPlaylist({...newPlaylist, title: e.target.value})}
                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:bg-white focus:border-primary transition-all font-bold placeholder:text-gray-300"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Description pédagogique</label>
                  <textarea
                    placeholder="Synthèse des objectifs du chapitre..."
                    value={newPlaylist.description}
                    onChange={(e) => setNewPlaylist({...newPlaylist, description: e.target.value})}
                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:bg-white focus:border-primary transition-all font-medium placeholder:text-gray-300 min-h-[140px]"
                  />
                </div>
            </DialogContent>
            <div className="flex justify-end gap-4 mt-12 p-0">
               <button onClick={handleCreateClose} className="px-8 py-4 text-[11px] font-black uppercase tracking-widest text-text/40 hover:text-text hover:bg-gray-50 rounded-2xl transition-all duration-300">Annuler</button>
               <button onClick={handleCreatePlaylist} className="px-10 py-4 text-[11px] font-black uppercase tracking-widest text-white bg-primary rounded-2xl shadow-2xl shadow-primary/20 hover:-translate-y-1 transition-all duration-300">Créer le module</button>
            </div>
          </Box>
        </Dialog>

        {/* Dialog pour modifier un chapitre */}
        <Dialog 
          open={openEditDialog} 
          onClose={handleEditClose} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: '2.5rem', p: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 40px 100px rgba(0,0,0,0.15)' } }}
        >
          <Box className="p-4">
            <DialogTitle className="flex items-center justify-between !p-0 mb-10">
              <div className="flex flex-col gap-1">
                 <span className="text-[10px] font-black text-blue-500 uppercase tracking-[0.3em]">Mise à Jour du Module</span>
                 <p className="text-3xl text-title font-black uppercase tracking-tight">Modifier le Chapitre</p>
              </div>
              <IconButton onClick={handleEditClose} className="!bg-gray-50 hover:!bg-white hover:shadow-xl transition-all">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent className="!p-0 space-y-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Titre du chapitre *</label>
                  <input
                    type="text"
                    placeholder="Titre du cours..."
                    value={editPlaylist.title}
                    onChange={(e) => setEditPlaylist({...editPlaylist, title: e.target.value})}
                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:border-blue-500 transition-all font-bold"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Description pédagogique</label>
                  <textarea
                    placeholder="Nouveaux objectifs du chapitre..."
                    value={editPlaylist.description}
                    onChange={(e) => setEditPlaylist({...editPlaylist, description: e.target.value})}
                    className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:border-blue-500 transition-all font-medium min-h-[140px]"
                  />
                </div>
            </DialogContent>
            <div className="flex justify-end gap-4 mt-12 p-0">
               <button onClick={handleEditClose} className="px-8 py-4 text-[11px] font-black uppercase tracking-widest text-text/40 hover:text-text hover:bg-gray-50 rounded-2xl transition-all duration-300">Annuler</button>
               <button onClick={handleUpdatePlaylist} className="px-10 py-4 text-[11px] font-black uppercase tracking-widest text-white bg-blue-500 rounded-2xl shadow-2xl shadow-blue-500/20 hover:-translate-y-1 transition-all duration-300">Enregistrer les modifications</button>
            </div>
          </Box>
        </Dialog>

        {/* Dialog pour ajouter un fichier */}
        <Dialog 
          open={openAddFileDialog} 
          onClose={handleAddFileClose} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: '2.5rem', p: 2, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 40px 100px rgba(0,0,0,0.15)' } }}
        >
          <Box className="p-4">
            <DialogTitle className="flex items-center justify-between !p-0 mb-10">
              <div className="flex flex-col gap-1">
                 <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em]">Ajout de Contenu</span>
                 <p className="text-3xl text-title font-black uppercase tracking-tight">Nouvelle Ressource</p>
              </div>
              <IconButton onClick={handleAddFileClose} className="!bg-gray-50 hover:!bg-white hover:shadow-xl transition-all">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent className="!p-0 space-y-8">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                   <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-primary shadow-sm">
                      <FontAwesomeIcon icon={faBookOpen} />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-[9px] font-black text-text/30 uppercase tracking-widest">Destination</span>
                      <span className="text-[11px] font-black text-title uppercase">{activePlaylist?.title} • <span className="text-primary italic">{activeStatus}</span></span>
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Nom de la ressource *</label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Ex: Cours Magistral 01"
                      value={newFileData.title}
                      onChange={handleAddFileInputChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all font-bold placeholder:text-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Type de contenu *</label>
                    <select
                      name="segment"
                      value={newFileData.segment}
                      onChange={handleAddFileInputChange}
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all font-black uppercase text-[10px] tracking-widest"
                    >
                      <option value="video">Vidéo</option>
                      <option value="youtube">Vidéo YouTube</option>
                      <option value="qcm">QCM</option>
                      <option value="fiche">Support PDF</option>
                      <option value="exercice">Exercice</option>
                      <option value="correction">Correction</option>
                    </select>
                  </div>
                </div>

                {newFileData.segment === "youtube" ? (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Lien Vidéo YouTube *</label>
                    <input
                      type="url"
                      name="youtubeUrl"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={newFileData.youtubeUrl}
                      onChange={handleAddFileInputChange}
                      className="w-full px-5 py-3.5 bg-blue-50/30 border border-blue-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold text-blue-600 placeholder:text-blue-200"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1">Sélectionner un fichier *</label>
                    <div className="relative group">
                      <input
                        type="file"
                        onChange={handleAddFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      <div className="w-full p-8 border-2 border-dashed border-gray-100 rounded-[2rem] bg-gray-50 group-hover:bg-white group-hover:border-primary/50 transition-all duration-500 flex flex-col items-center justify-center text-center">
                         {!selectedFile ? (
                           <>
                             <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-primary shadow-sm mb-4">
                                <AddIcon />
                             </div>
                             <p className="text-[11px] font-black text-title uppercase tracking-widest">Cliquez ou glissez un fichier</p>
                             <p className="text-[9px] font-medium text-text/40 mt-1 uppercase tracking-widest">Vidéo (MP4) ou Document (PDF)</p>
                           </>
                         ) : (
                           <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                             <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner mb-4">
                                <FontAwesomeIcon 
                                  icon={selectedFile.type.includes('pdf') ? faFilePdf : faPlayCircle} 
                                  className="text-2xl" 
                                />
                             </div>
                             <p className="text-[12px] font-black text-title uppercase tracking-tight max-w-[200px] truncate">{selectedFile.name}</p>
                             <p className="text-[9px] font-black text-primary/60 mt-1 uppercase tracking-[0.2em]">
                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • Prêt pour l'envoi
                             </p>
                           </div>
                         )}
                      </div>
                    </div>
                  </div>
                )}

                {uploadingFile && (
                  <div className="p-6 bg-primary/5 rounded-3xl border border-primary/10 space-y-4">
                    <div className="flex items-center justify-between">
                       <span className="text-[10px] font-black text-primary uppercase tracking-widest">Téléversement en cours</span>
                       <span className="text-xl font-black text-primary">{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden p-0.5">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-[#02c39a] transition-all duration-300 shadow-[0_0_15px_rgba(9,116,95,0.3)]"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
            </DialogContent>
            <div className="flex justify-end gap-4 mt-12 p-0">
               <button onClick={handleAddFileClose} disabled={uploadingFile} className="px-8 py-4 text-[11px] font-black uppercase tracking-widest text-text/40 hover:text-text hover:bg-gray-50 rounded-2xl transition-all duration-300 disabled:opacity-30">Annuler</button>
               <button onClick={handleUploadFile} disabled={uploadingFile} className="px-10 py-4 text-[11px] font-black uppercase tracking-widest text-white bg-primary rounded-2xl shadow-2xl shadow-primary/20 hover:-translate-y-1 transition-all duration-300 disabled:bg-gray-200">
                  {uploadingFile ? "Traitement..." : "Téléverser la ressource"}
               </button>
            </div>
          </Box>
        </Dialog>

        {/* Dialog pour supprimer un fichier */}
        <Dialog 
          open={openDeleteFileDialog} 
          onClose={handleDeleteFileClose} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{ 
            sx: { 
              borderRadius: '2.5rem', 
              p: 0, 
              border: '1px solid rgba(0,0,0,0.05)', 
              boxShadow: '0 40px 100px rgba(0,0,0,0.15)',
              overflow: 'hidden'
            } 
          }}
        >
          <div className="relative overflow-hidden bg-white">
            {/* Top accent bar */}
            <div className="h-2 w-full bg-gradient-to-r from-red-600 via-rose-500 to-orange-500"></div>
            
            <div className="p-8 md:p-12">
              <div className="flex flex-col items-center text-center">
                 {/* Glowing Icon Container */}
                 <div className="w-24 h-24 bg-red-50 rounded-[2rem] flex items-center justify-center text-red-500 mb-8 relative group">
                    <div className="absolute inset-0 bg-red-500/20 rounded-[2rem] blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-60"></div>
                    <div className="relative w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl shadow-red-500/10 border border-red-100">
                       <FontAwesomeIcon icon={faTrash} className="text-2xl" />
                    </div>
                 </div>

                 <div className="space-y-3">
                   <h5 className="text-[10px] font-black text-rose-500 uppercase tracking-[0.4em]">Action Irréversible</h5>
                   <h3 className="text-2xl md:text-3xl font-black text-title uppercase tracking-tight">Supprimer la Ressource</h3>
                 </div>

                 <div className="mt-8 p-6 bg-red-50/50 rounded-3xl border border-red-100/50 max-w-sm">
                    <p className="text-sm font-bold text-text/60 leading-relaxed text-center">
                       Attention : cette opération supprimera <span className="text-title font-black underline decoration-red-500/30 decoration-2">définitivement</span> cette ressource. Vous ne pourrez pas la récupérer par la suite.
                    </p>
                 </div>

                 <div className="flex flex-col sm:flex-row gap-4 w-full mt-10">
                   <button 
                     onClick={handleDeleteFileClose}
                     className="flex-1 py-5 bg-white border-2 border-gray-100 text-title font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-95"
                   >
                     Annuler
                   </button>
                   <button 
                     onClick={handleDeleteFile}
                     className="flex-1 py-5 bg-[#E11D48] text-white font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-red-500/40 hover:bg-red-700 hover:-translate-y-1 transition-all active:scale-95 shadow-[0_20px_40px_-10px_rgba(225,29,72,0.4)]"
                   >
                     Supprimer
                   </button>
                 </div>
              </div>
            </div>
            
            <IconButton 
              onClick={handleDeleteFileClose} 
              className="!absolute top-6 right-6 !bg-gray-50 hover:!bg-red-50 hover:!text-red-500 transition-all !w-10 !h-10 border border-gray-100"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* Background decorative blobs */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-500/5 blur-[100px] pointer-events-none rounded-full"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orange-500/5 blur-[100px] pointer-events-none rounded-full"></div>
          </div>
        </Dialog>

        <Dialog 
          open={openDocDialog} 
          onClose={handleCloseDoc} 
          fullScreen
          PaperProps={{ sx: { bgcolor: '#1a1a1a' } }}
        >
           <Box className="h-full flex flex-col">
              <div className="px-8 py-4 flex justify-between bg-zinc-950 items-center border-b border-white/5 shadow-2xl">
                 <div className="flex flex-col gap-0.5">
                    <span className="text-[9px] font-black text-primary uppercase tracking-[0.3em]">Mode Consultation</span>
                    <h3 className="text-white font-black uppercase tracking-tight text-lg">Bibliothèque de Ressources</h3>
                 </div>
                 <div className="flex items-center gap-4">
                    <button 
                      onClick={() => window.open(docUrl, '_blank')}
                      className="px-6 py-2.5 bg-white/5 hover:bg-white/10 text-white font-black text-[10px] uppercase tracking-widest rounded-xl transition-all border border-white/5"
                    >
                      Ouvrir plein écran / Télécharger
                    </button>
                    <IconButton onClick={handleCloseDoc} className="!bg-white/5 !text-white hover:!bg-red-500 transition-all !w-12 !h-12 !rounded-2xl">
                        <CloseIcon />
                    </IconButton>
                 </div>
              </div>
              <div className="flex-1 bg-[#1a1a1a] p-4 md:p-8 relative">
                 {fetchingDoc ? (
                   <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex flex-col items-center gap-4">
                         <CircularProgress sx={{ color: '#09745f' }} />
                         <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40">Lecture sécurisée...</p>
                      </div>
                   </div>
                 ) : (
                   <div className="w-full h-full bg-white rounded-3xl overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.5)] border border-white/5">
                      <iframe 
                        src={docUrl} 
                        className="w-full h-full border-none" 
                        title="Document Viewer"
                      />
                   </div>
                 )}
              </div>
           </Box>
        </Dialog>

        <Snackbar
          open={localSnackbar.open}
          autoHideDuration={6000}
          onClose={() => setLocalSnackbar({...localSnackbar, open: false})}
        >
          <Alert 
            onClose={() => setLocalSnackbar({...localSnackbar, open: false})}
            severity={localSnackbar.severity}
            sx={{ width: '100%' }}
          >
            {localSnackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </DashboardLayout>
  );
};

export default SubjectDetails;
